import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const ArchiveContainer = styled.div`
  background: var(--dark);
  padding: 40px 20px;
  min-height: 100vh;
  color: var(--light);
`;

const ArchiveHeader = styled.h1`
  text-align: center;
  color: var(--primary);
  margin-bottom: 40px;
  font-size: 2.5rem;
  font-weight: 700;
`;

const ArticleList = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const ArchiveItem = styled.div`
  background: var(--card-bg);
  border-radius: var(--border-radius);
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: var(--shadow);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  }

  h2 {
    color: var(--primary);
    font-size: 1.8rem;
    margin-bottom: 12px;
  }

  h3 {
    color: var(--accent);
    font-size: 1.2rem;
    margin-bottom: 16px;
  }

  .meta {
    color: #a0aec0;
    font-size: 0.95rem;
    margin-top: 16px;
    font-style: italic;
  }

  @media (max-width: 768px) {
    padding: 18px;

    h2 {
      font-size: 1.5rem;
    }

    h3 {
      font-size: 1.1rem;
    }
  }
`;

const BackButton = styled.button`
  background: var(--primary);
  color: var(--dark);
  border: none;
  padding: 12px 28px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 32px;
  transition: 0.3s ease;
  display: block;
  margin-left: auto;
  margin-right: auto;

  &:hover {
    background: var(--accent);
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const NoArticles = styled.p`
  text-align: center;
  color: #718096;
  font-size: 1.2rem;
  margin-top: 40px;
`;

const ArchivedArticles = () => {
  const navigate = useNavigate();
  const [archivedArticles, setArchivedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArchivedArticles = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('archived_articles')
        .select('article_id, title, subheader, archived_at')
        .order('archived_at', { ascending: false });

      if (error) {
        console.error('Error fetching archived articles:', error);
      } else {
        setArchivedArticles(data || []);
      }
      setLoading(false);
    };

    fetchArchivedArticles();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <ArchiveContainer>
      <BackButton onClick={() => navigate('/')}>Back to Home</BackButton>

      <ArchiveHeader>Archived Articles</ArchiveHeader>

      {loading ? (
        <p style={{ textAlign: 'center', color: '#a0aec0' }}>Loading archived articles...</p>
      ) : archivedArticles.length === 0 ? (
        <NoArticles>No archived articles found.</NoArticles>
      ) : (
        <ArticleList>
          {archivedArticles.map((article) => (
            <ArchiveItem
              key={article.article_id}
              onClick={() => navigate(`/archived-articles/${article.article_id}`)}
            >
              <h2>{article.title}</h2>
              {article.subheader && <h3>{article.subheader}</h3>}
              <div className="meta">
                Archived on: {formatDate(article.archived_at)}
              </div>
            </ArchiveItem>
          ))}
        </ArticleList>
      )}
    </ArchiveContainer>
  );
};

export default ArchivedArticles;